/**
 * A folder on this computer that every take is copied into once it ends (Chrome and Edge, which let a page write
 * to a folder the person chose). Recording still goes into the browser's own storage first: it survives a crash
 * mid-take, where a file written straight into a folder would be lost. The copy happens once the files are
 * closed.
 *
 * The folder is remembered in IndexedDB. Browsers may ask again for permission to write to it; that needs a
 * click, so when it's needed the end-of-take note offers one.
 */

import { Observable } from './emitter.ts';

const DB = 'wordstow';
const STORE = 'handles';
const KEY = 'save-folder';
const SAVED_KEY = 'wordstow.savedTakes';

export const canUseFolders = () => typeof (window as any).showDirectoryPicker === 'function';

function idb<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(STORE);
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => open.result.close();
    };
  });
}

export type FolderAccess = 'granted' | 'prompt' | 'denied';

class SaveFolder extends Observable {
  handle: FileSystemDirectoryHandle | null = null;
  access: FolderAccess = 'prompt';
  loaded = false;
  /** Takes already copied, by journal name: where and when. */
  saved: Record<string, { folder: string; at: number }> = {};

  constructor() {
    super();
    try {
      this.saved = JSON.parse(localStorage.getItem(SAVED_KEY) ?? '{}');
    } catch {}
    if (canUseFolders()) void this.load();
  }

  private async load() {
    try {
      this.handle = (await idb('readonly', (s) => s.get(KEY))) ?? null;
      if (this.handle) this.access = await (this.handle as any).queryPermission({ mode: 'readwrite' });
    } catch {
      this.handle = null;
    }
    this.loaded = true;
    this.changed();
  }

  /** Opens the folder picker (needs a click). */
  async choose() {
    const handle: FileSystemDirectoryHandle = await (window as any).showDirectoryPicker({ id: 'wordstow', mode: 'readwrite', startIn: 'documents' });
    await idb('readwrite', (s) => s.put(handle, KEY));
    this.handle = handle;
    this.access = 'granted';
    this.changed();
  }

  async forget() {
    await idb('readwrite', (s) => s.delete(KEY)).catch(() => {});
    this.handle = null;
    this.changed();
  }

  /** Permission to write, asking if needed (asking needs a click). */
  async allow(): Promise<boolean> {
    if (!this.handle) return false;
    try {
      this.access = await (this.handle as any).requestPermission({ mode: 'readwrite' });
    } catch {
      this.access = 'prompt';
    }
    this.changed();
    return this.access === 'granted';
  }

  /** Writable now, without asking. */
  async ready(): Promise<FileSystemDirectoryHandle | null> {
    if (!this.handle) return null;
    try {
      this.access = await (this.handle as any).queryPermission({ mode: 'readwrite' });
    } catch {
      this.access = 'prompt';
    }
    this.changed();
    return this.access === 'granted' ? this.handle : null;
  }

  markSaved(journalName: string, folder: string) {
    this.saved = { ...this.saved, [journalName]: { folder, at: Date.now() } };
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(this.saved));
    } catch {}
    this.changed();
  }
}

let instance: SaveFolder | null = null;
export const saveFolder = () => (instance ??= new SaveFolder());
